import * as React from 'react';

import { cn } from '../lib/utils';
import {
  ChartDataTable,
  summarizeSeries,
  type ChartTableSeries,
} from './charts/chart-data-table';

export interface BarChartSeries {
  name: string;
  data: number[];
  color?: string;
}

export interface BarChartProps extends React.HTMLAttributes<HTMLDivElement> {
  series: BarChartSeries[];
  labels?: string[];
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  /** Stack the series (grouped by default) */
  stacked?: boolean;
}

const DEFAULT_PALETTE = [
  'hsl(var(--primary))',
  'hsl(var(--state-running))',
  'hsl(var(--alarm-minor))',
  'hsl(var(--alarm-advisory))',
  'hsl(var(--state-maintenance))',
];

/**
 * Lightweight SVG bar chart using design tokens.
 * Supports grouped or stacked multi-series.
 */
export const BarChart = React.forwardRef<HTMLDivElement, BarChartProps>(
  (
    {
      series,
      labels,
      height = 200,
      showGrid = true,
      showLegend = true,
      stacked = false,
      className,
      ...props
    },
    ref
  ) => {
    const width = 600;
    const padding = { top: 20, right: 20, bottom: 40, left: 40 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const categoryCount = series[0]?.data.length || 0;
    if (categoryCount === 0) return null;

    // Compute max (stacked sums or grouped max)
    const maxValue = stacked
      ? Math.max(
          ...Array.from({ length: categoryCount }, (_, i) =>
            series.reduce((sum, s) => sum + (s.data[i] || 0), 0)
          )
        )
      : Math.max(...series.flatMap((s) => s.data));
    const valueMax = maxValue * 1.1;

    const categoryWidth = chartWidth / categoryCount;
    const barsPerCategory = stacked ? 1 : series.length;
    const barWidth = (categoryWidth * 0.7) / barsPerCategory;
    const barSpacing = (categoryWidth * 0.3) / 2;

    const gridLines = Array.from({ length: 5 }, (_, i) => {
      const y = padding.top + (i / 4) * chartHeight;
      const value = (1 - i / 4) * valueMax;
      return { y, value };
    });

    const yFor = (v: number) =>
      padding.top + (1 - v / valueMax) * chartHeight;

    // ── Textual equivalent (WCAG 1.1.1) ────────────────────────────────
    // Same `series`/`labels` the bars are drawn from, so the table cannot
    // drift from the picture.
    const tableSeries: ChartTableSeries[] = series.map((s) => ({
      id: s.name,
      label: s.name,
      points: s.data.map((v, i) => ({
        label: labels?.[i] ?? `#${i + 1}`,
        value: Number.isFinite(v) ? v : null,
      })),
    }));

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          data-slot="chart-canvas"
          role="img"
          /* Was "Bar chart" — a name for the picture, not its content. */
          aria-label={summarizeSeries('Bar chart', tableSeries)}
        >
          {showGrid &&
            gridLines.map((line, i) => (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={line.y}
                  x2={width - padding.right}
                  y2={line.y}
                  stroke="hsl(var(--border))"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
                <text
                  x={padding.left - 6}
                  y={line.y + 3}
                  className="fill-muted-foreground"
                  style={{ fontSize: 10 }}
                  textAnchor="end"
                >
                  {line.value.toFixed(1)}
                </text>
              </g>
            ))}

          {Array.from({ length: categoryCount }).map((_, categoryIndex) => {
            const categoryX = padding.left + categoryIndex * categoryWidth;
            let stackOffset = 0;
            return series.map((s, seriesIndex) => {
              const value = s.data[categoryIndex] || 0;
              const barHeight = (value / valueMax) * chartHeight;
              const color =
                s.color ?? DEFAULT_PALETTE[seriesIndex % DEFAULT_PALETTE.length];
              const x = stacked
                ? categoryX + barSpacing
                : categoryX + barSpacing + seriesIndex * barWidth;
              const y = stacked
                ? padding.top + chartHeight - stackOffset - barHeight
                : yFor(value);

              if (stacked) stackOffset += barHeight;

              return (
                <rect
                  key={`${categoryIndex}-${seriesIndex}`}
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={color}
                  rx={2}
                />
              );
            });
          })}

          {labels && (
            <g>
              {labels.map((label, i) => (
                <text
                  key={i}
                  x={padding.left + i * categoryWidth + categoryWidth / 2}
                  y={height - padding.bottom + 15}
                  textAnchor="middle"
                  className="fill-muted-foreground"
                  style={{ fontSize: 10 }}
                >
                  {label}
                </text>
              ))}
            </g>
          )}
        </svg>

        {showLegend && series.length > 1 && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-xs">
            {series.map((s, i) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3 rounded-sm"
                  style={{
                    backgroundColor:
                      s.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length],
                  }}
                />
                <span className="text-muted-foreground">{s.name}</span>
              </div>
            ))}
          </div>
        )}

        <ChartDataTable caption="Bar chart" series={tableSeries} />
      </div>
    );
  }
);

BarChart.displayName = 'BarChart';
